/**
 * WUI-408: чистые хелперы для Tiles setInlineVisibility (тестируемые без mount Vue).
 */

export type TilesInlineScopeLike = {
  tableFieldName?: string
  rowIndex?: number
  tilesElementId?: string | number
  parentPanelId?: string | number
}

/** Row-local override видимости action/element по имени внутри строки tiles. */
export type TilesRowActionVisibilityMap = Record<number, Record<string, boolean>>

/**
 * Общий store, но бакет на открытую форму и конкретный Tiles.
 * Один rowIndex на всё приложение: «Просмотр» чужого заказа включает кнопки
 * черновика, пока обе формы смонтированы (слоты платформы прячутся через v-show).
 */
export type TilesRowActionVisibilityStore = Record<string, TilesRowActionVisibilityMap>

/** Стабильная пустая map: getter computed не должен создавать новый объект. */
export const EMPTY_TILES_ROW_ACTION_VISIBILITY: TilesRowActionVisibilityMap = {}

/** Ключ бакета: слот страницы + поле таблицы + id элемента Tiles. */
export function buildTilesRowActionVisibilityBucketKey(input: {
  pageDataSlot?: string | number | null
  formId?: string | number | null
  tableFieldName?: string | number | null
  tilesElementId?: string | number | null
}): string {
  const slot = String(input.pageDataSlot ?? '').trim() || String(input.formId ?? '').trim() || '_'
  const table = String(input.tableFieldName ?? '').trim() || '_'
  const elementId = String(input.tilesElementId ?? '').trim() || '_'
  return `${slot}::${table}::${elementId}`
}

export function readTilesRowActionVisibilityBucket(
  store: TilesRowActionVisibilityStore | null | undefined,
  bucketKey: string,
): TilesRowActionVisibilityMap {
  const key = String(bucketKey ?? '').trim()
  if (!store || !key) return EMPTY_TILES_ROW_ACTION_VISIBILITY
  return store[key] ?? EMPTY_TILES_ROW_ACTION_VISIBILITY
}

export function writeTilesRowActionVisibilityBucket(
  store: TilesRowActionVisibilityStore | null | undefined,
  bucketKey: string,
  map: TilesRowActionVisibilityMap,
): TilesRowActionVisibilityStore {
  const key = String(bucketKey ?? '').trim()
  const prev = store ?? {}
  if (!key) return prev
  return { ...prev, [key]: map }
}

/** Registry key вида `table__rowN__elementName` (Tiles inlineAfterLoad / setInlineVisibility). */
export function isTilesRowScopedRegistryName(name: string): boolean {
  return /^.+__row\d+__.+/.test(String(name ?? '').trim())
}

/** Прочитать row-local visibility action */
export function getTilesRowActionVisibility(
  map: TilesRowActionVisibilityMap | undefined | null,
  rowIndex: number,
  actionName: string,
): boolean | undefined {
  if (!map || !Number.isFinite(rowIndex) || rowIndex < 0) return undefined
  const name = String(actionName ?? '').trim()
  if (!name) return undefined
  const rowMap = map[rowIndex]
  if (!rowMap || typeof rowMap !== 'object') return undefined
  return rowMap[name]
}

/** Есть ли у строки tiles хотя бы одна запись из setInlineVisibility (inlineAfterLoad отработал). */
export function tilesRowHasInlineVisibilityEntries(
  map: TilesRowActionVisibilityMap | undefined | null,
  rowIndex: number,
): boolean {
  if (!map || !Number.isFinite(rowIndex) || rowIndex < 0) return false
  const rowMap = map[rowIndex]
  return Boolean(rowMap && typeof rowMap === 'object' && Object.keys(rowMap).length > 0)
}

export type ResolveTilesInlineTargetVisibleInput = {
  elementName: string
  rowIndex: number
  hasInlineAfterLoad: boolean
  targets?: ReadonlySet<string> | null
  rowMap?: TilesRowActionVisibilityMap | null
  sliceVisible?: boolean
  getterVisible?: boolean
  persistedVisible?: boolean
}

/**
 * Видимость action/element клона, если имя есть в setInlineVisibility.
 * Map / slice / getter побеждают.
 * Hide-until-ready (false) — только первый paint строки без записей в map
 * (не мигать default XML). После reload map не обнулять: прошлое значение
 * живёт, пока setInlineVisibility не перезапишет.
 * Не-target → undefined, caller оставляет свой default.
 */
export function resolveTilesInlineTargetVisible(
  input: ResolveTilesInlineTargetVisibleInput,
): boolean | undefined {
  const name = String(input.elementName ?? '').trim()
  const rowIndex = Number(input.rowIndex)
  if (!name || !Number.isFinite(rowIndex) || rowIndex < 0) return undefined

  const fromMap = getTilesRowActionVisibility(input.rowMap, rowIndex, name)
  if (fromMap !== undefined) return fromMap
  if (input.sliceVisible !== undefined) return input.sliceVisible
  if (input.getterVisible !== undefined) return input.getterVisible

  if (!input.hasInlineAfterLoad || !input.targets?.has(name)) return undefined
  if (!tilesRowHasInlineVisibilityEntries(input.rowMap, rowIndex)) return false
  if (input.persistedVisible !== undefined) return input.persistedVisible
  return false
}

export function setTilesRowActionVisibility(
  map: TilesRowActionVisibilityMap,
  rowIndex: number,
  actionName: string,
  visible: boolean,
): TilesRowActionVisibilityMap {
  const name = String(actionName ?? '').trim()
  if (!name || !Number.isFinite(rowIndex) || rowIndex < 0) return map
  const prevRow = map[rowIndex] ?? {}
  return {
    ...map,
    [rowIndex]: { ...prevRow, [name]: visible },
  }
}

/** Полный сброс row-local map. Не вызывать на reload/panel: пустой map прячет все target. */
export function clearTilesRowActionVisibilityMap(
  _map?: TilesRowActionVisibilityMap | null,
): TilesRowActionVisibilityMap {
  return {}
}

/**
 * Оставить только строки с index < rowCount.
 * Reload не должен обнулять map целиком — иначе chrome пропадает до setInlineVisibility.
 */
export function retainTilesRowActionVisibilityUpToRowCount(
  map: TilesRowActionVisibilityMap | null | undefined,
  rowCount: number,
): TilesRowActionVisibilityMap {
  if (!map) return {}
  if (!Number.isFinite(rowCount) || rowCount < 0) {
    return { ...map }
  }
  const next: TilesRowActionVisibilityMap = {}
  for (const [key, row] of Object.entries(map)) {
    const idx = Number(key)
    if (!Number.isFinite(idx) || idx < 0 || idx >= rowCount) continue
    next[idx] = row
  }
  return next
}

/**
 * Ключи persistentScopedElementVisibility для tiles-таблиц (`table__rowN__name`).
 */
export function listPersistentScopedKeysForTableFields(
  persistentKeys: Iterable<string>,
  tableFieldNames: ReadonlySet<string>,
): string[] {
  if (!tableFieldNames || tableFieldNames.size === 0) return []
  const out: string[] = []
  for (const raw of persistentKeys) {
    const key = String(raw ?? '').trim()
    if (!key || !isTilesRowScopedRegistryName(key)) continue
    for (const table of tableFieldNames) {
      const prefix = `${table}__row`
      if (key.startsWith(prefix)) {
        out.push(key)
        break
      }
    }
  }
  return out
}

export function omitPersistentScopedKeys(
  persistent: Record<string, boolean>,
  keysToOmit: ReadonlyArray<string>,
): Record<string, boolean> {
  if (!keysToOmit.length) return persistent
  const omit = new Set(keysToOmit)
  const next: Record<string, boolean> = {}
  for (const [key, value] of Object.entries(persistent)) {
    if (!omit.has(key)) next[key] = value
  }
  return next
}

const TILES_ROW_SCOPED_KEY_RE = /^(.+)__row(\d+)__(.+)$/

/**
 * Persist-ключи исчезнувших строк (`rowIndex >= rowCount` этой таблицы).
 * Чужие таблицы и живые индексы не трогаем — иначе chrome пропадает до script.
 */
export function listPersistentScopedKeysBeyondRowCount(
  persistentKeys: Iterable<string>,
  tableRowCounts: ReadonlyMap<string, number>,
): string[] {
  if (!tableRowCounts || tableRowCounts.size === 0) return []
  const out: string[] = []
  for (const raw of persistentKeys) {
    const key = String(raw ?? '').trim()
    if (!key) continue
    const match = TILES_ROW_SCOPED_KEY_RE.exec(key)
    if (!match) continue
    const table = match[1]
    const rowIndex = Number(match[2])
    if (!tableRowCounts.has(table)) continue
    const rowCount = tableRowCounts.get(table)
    if (!Number.isFinite(rowIndex) || rowIndex < 0 || rowCount == null) continue
    if (rowIndex >= rowCount) out.push(key)
  }
  return out
}

/** Registry-имя элемента/action внутри строки tiles. */
export function buildTilesInlineElementVisibilityName(
  tableFieldName: string,
  rowIndex: number,
  elementName: string,
): string {
  const table = String(tableFieldName ?? '').trim()
  const name = String(elementName ?? '').trim()
  if (!table || !name || !Number.isFinite(rowIndex) || rowIndex < 0) {
    return name
  }
  return `${table}__row${rowIndex}__${name}`
}

/**
 * Видимость обёртки строки: override из setInlineVisibility(boolean) или expandByDefault.
 */
export function resolveTilesRowWrapVisibility(
  forcedRowVisible: boolean | undefined,
  expandByDefault: boolean,
): boolean {
  if (forcedRowVisible !== undefined) return forcedRowVisible !== false
  return expandByDefault
}

export function resolveTilesInlineAfterLoadRowIndex(
  componentRowIndex: number | null | undefined,
  enRowScope: TilesInlineScopeLike | null | undefined,
  instance: { tableFieldName?: string; tilesElementId?: string | number },
): number | null {
  if (
    componentRowIndex !== null &&
    componentRowIndex !== undefined &&
    Number.isFinite(componentRowIndex) &&
    componentRowIndex >= 0
  ) {
    return componentRowIndex
  }

  const scope = enRowScope
  if (!scope) return null

  const tableFieldName = String(scope.tableFieldName ?? '').trim()
  const instanceTable = String(instance.tableFieldName ?? '').trim()
  if (!tableFieldName || tableFieldName !== instanceTable) return null

  const scopeElementId = Number(scope.tilesElementId)
  const instanceElementId = Number(instance.tilesElementId)
  if (
    Number.isFinite(scopeElementId) &&
    scopeElementId > 0 &&
    Number.isFinite(instanceElementId) &&
    instanceElementId > 0 &&
    scopeElementId !== instanceElementId
  ) {
    return null
  }

  const rowIndex = Number(scope.rowIndex)
  return Number.isFinite(rowIndex) && rowIndex >= 0 ? rowIndex : null
}
